import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import userStorage, { SESSION_KEYS, CLEANUP_KEYS } from '@/lib/userStorage';
import { clearAdminCaches } from '@/lib/admin-cache';
import { clearSubmitCmsCaches } from '@/lib/attestations';
import { queryClient } from '@/lib/query-client';
import { clearOutcomeCache } from '@/lib/measure-outcomes';

interface User {
  id: number;
  email: string;
  name: string;
  role?: string;
}

interface Practice {
  id: number;
  tin: string;
  tin_name: string;
  practice_alias?: string;
}

interface Client {
  id: number;
  client_alias: string;
  account_id?: string;
  /** Caller's tenancy role on this client: 'owner' | 'manager' | 'user' */
  user_role?: string;
  practices: Practice[];
}

interface LastSelections {
  last_client_id: number | null;
  last_practice_id: number | null;
}

interface AuthContextType {
  user: User | null;
  clients: Client[];
  setClients: React.Dispatch<React.SetStateAction<Client[]>>; // Expose setter
  lastSelections: LastSelections | null;
  setLastSelections: React.Dispatch<React.SetStateAction<LastSelections | null>>;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
  mustChangePassword: boolean;
  setMustChangePassword: React.Dispatch<React.SetStateAction<boolean>>;
  applyNewToken: (token: string) => void;
  /** Refetch the client list (e.g. after accepting an access invitation). */
  refreshClients: () => Promise<void>;
  selectedClient: Client | null;
  setSelectedClient: React.Dispatch<React.SetStateAction<Client | null>>;
  selectedPractice: Practice | null;
  setSelectedPractice: React.Dispatch<React.SetStateAction<Practice | null>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [clients, setClients] = useState<Client[]>([]); // Use new interface
  const [lastSelections, setLastSelections] = useState<LastSelections | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedPractice, setSelectedPractice] = useState<Practice | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  // Swap in a freshly-issued token (e.g. after a password change) without a full re-login.
  const applyNewToken = (newToken: string) => {
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
  };

  // Check for existing token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    if (savedToken) {
      setToken(savedToken);
      // Verify token and get user data
      verifyToken(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify: string) => {
    try {
      // First, get user's own data and last selections
      const meResponse = await fetch(`${apiUrl}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${tokenToVerify}` },
      });

      if (meResponse.ok) {
        const meData = await meResponse.json();
        setUser(meData.user);
        setMustChangePassword(!!meData.user?.must_change_password);
        setLastSelections(meData.lastSelections); // Save last selections
        setToken(tokenToVerify);
        
        // Initialize userStorage with the user
        userStorage.setUser(meData.user);

        // After successfully getting user, fetch their clients
        const clientsResponse = await fetch(`${apiUrl}/api/clients`, {
          headers: { 'Authorization': `Bearer ${tokenToVerify}` },
        });
        if (clientsResponse.ok) {
          const clientsData = await clientsResponse.json();
          setClients(clientsData);
          
          // Restore selected practice from sessionStorage (not localStorage)
          const savedPracticeId = userStorage.getSessionItem('selectedPracticeId');
          const savedClientId = userStorage.getSessionItem('selectedClientId');
          
          console.log('[AuthContext] Attempting to restore practice - savedPracticeId:', savedPracticeId, 'savedClientId:', savedClientId);
          
          if (savedPracticeId && savedClientId && clientsData.length > 0) {
            // Find the matching client and practice
            const client = clientsData.find((c: Client) => c.id === parseInt(savedClientId));
            if (client) {
              const practice = client.practices.find((p: Practice) => p.id === parseInt(savedPracticeId));
              if (practice) {
                console.log('[AuthContext] Successfully restoring practice:', practice.practice_alias || practice.tin_name, 'ID:', practice.id);
                setSelectedClient(client);
                setSelectedPractice(practice);
              } else {
                console.log('[AuthContext] Practice not found in client practices');
              }
            } else {
              console.log('[AuthContext] Client not found in clientsData');
            }
          } else {
            console.log('[AuthContext] Not restoring - missing data or no saved selection');
          }
        } else {
          // Handle case where clients fetch fails but user is valid
          setClients([]);
        }
      } else {
        // Token is invalid for /me endpoint
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
        setClients([]);
        setLastSelections(null);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
      setClients([]);
      setLastSelections(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${apiUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Login failed');
      }

      const data = await response.json();
      
      // A prior session in this tab may have ended without logout() (closed
      // laptop, expired token) — never serve its cached patient rows.
      clearOutcomeCache();

      setUser(data.user);
      setMustChangePassword(!!data.mustChangePassword);
      // Initialize userStorage with the logged-in user
      userStorage.setUser(data.user);
      
      // Clean up any old global keys from previous sessions
      userStorage.cleanupGlobalKeys([
        'selectedPracticeId',
        'selectedClientId',
        'currentPractice',
      ]);
      
      // Migrate any old localStorage keys to user-scoped storage
      userStorage.migrateOldKeys([
        'gaps:colPresets',
        'gaps:activePresetId',
        'gaps:colSettings',
        'gaps:filters',
        'gaps:measureNotes',
        'gaps:measureEvidence',
      ]);
      
      // The login response from /api/auth/login does not include the full client list.
      // We will rely on verifyToken to fetch clients after login, or trigger it here.
      // For simplicity, we'll clear clients and let the page reload handle it.
      setClients([]); 
      setLastSelections(null);
      setToken(data.token);
      localStorage.setItem('auth_token', data.token);
      
      // Manually trigger data fetch after login. Raise `loading` for the
      // window where user/token are set but clients are still [] — without it
      // Home's view effect ran on the empty client list and flashed the
      // onboarding cards before the real list arrived (prod first-login bug,
      // 2026-07-21). verifyToken's finally block lowers it.
      setLoading(true);
      await verifyToken(data.token);

    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    // Clear user-scoped data before clearing user
    userStorage.clearUserData();
    userStorage.clearSessionData();

    // React Query cache can hold another account's practice/score data —
    // full wipe on logout (frontend-snappiness plan, Phase 0.1).
    queryClient.clear();

    // Admin caches hold names/emails — never carry them across accounts.
    clearAdminCaches();

    // Submit-to-CMS caches hold signer/clinician names — same rule.
    clearSubmitCmsCaches();

    // Module-level outcome caches hold patient rows (keyed by practice only).
    clearOutcomeCache();

    // P4 (security review S4): clear program-grant caches so tiles never
    // leak across accounts on a shared browser.
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith('program_grants_cache_'))
        .forEach(k => localStorage.removeItem(k));
    } catch { /* storage unavailable — nothing to clear */ }
    
    // Clean up any remaining global keys
    userStorage.cleanupGlobalKeys([
      'selectedPracticeId',
      'selectedClientId',
      'currentPractice',
      'gaps:colPresets',
      'gaps:activePresetId',
      'gaps:colSettings',
      'gaps:filters',
      'gaps:measureNotes',
      'gaps:measureEvidence',
      'gaps:selectedBaseIds',
      'gaps:additions',
    ]);
    
    // Clear user from storage utility
    userStorage.setUser(null);
    
    setUser(null);
    setClients([]);
    setLastSelections(null);
    setToken(null);
    setMustChangePassword(false);
    setSelectedClient(null);
    setSelectedPractice(null);
    
    // Clear auth token
    localStorage.removeItem('auth_token');
    
    // Optional: Call logout endpoint
    if (token) {
      fetch(`${apiUrl}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }).catch(console.error);
    }
  };

  // Refetch the client list without a full re-verify (used after accepting an
  // access invitation so the new org/practices appear immediately).
  const refreshClients = async () => {
    const activeToken = token || localStorage.getItem('auth_token');
    if (!activeToken) return;
    try {
      const response = await fetch(`${apiUrl}/api/clients`, {
        headers: { 'Authorization': `Bearer ${activeToken}` },
      });
      if (response.ok) {
        setClients(await response.json());
      }
    } catch (error) {
      console.error('Failed to refresh clients:', error);
    }
  };

  // A 401 from the API client (expired/invalidated session) drops to login.
  useEffect(() => {
    const onUnauthorized = () => logout();
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, []);

  // Idle auto-logout after 15 minutes of no user activity (HIPAA expectation).
  useEffect(() => {
    if (!token) return;
    let timer: ReturnType<typeof setTimeout>;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => logout(), 15 * 60 * 1000); // 15-min idle auto-logout
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [token]);

  const value: AuthContextType = {
    user,
    clients,
    setClients, // Add to context value
    lastSelections,
    setLastSelections,
    token,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    mustChangePassword,
    setMustChangePassword,
    applyNewToken,
    refreshClients,
    selectedClient,
    setSelectedClient,
    selectedPractice,
    setSelectedPractice,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

